import { FunctionComponent, useEffect, Dispatch } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { css } from '@emotion/css'
import { useTheme, Theme } from '@emotion/react'
import { useRouter } from 'next/router'
import { PaymentMethod } from '@/libs/helpers/apiClient'
import { formStyles } from '@/tbui'

const iframeDivStyles = (theme: Theme): string => css`
  margin-top: 8px;

  @media (min-width: ${theme.breakpoints.SM}) {
    max-width: 670px;
  }
`

const iframeStyles = (theme: Theme): IframeOptions['custom_style'] => ({
  styles: {
    base: {
      color: theme.palette.TEXT,
      border: `${formStyles(theme).border}`,
      'border-radius': `${formStyles(theme).radius}`,
      'font-weight': '200',
      'font-family': 'Arial',
      'font-size': `${formStyles(theme).fontSize}`,
      padding: `${formStyles(theme).padding}`,
      '::placeholder': {
        'text-transform': 'lowercase',
        color: theme.palette.GRAY_1,
      },
    },

    invalid: {
      padding: `${formStyles(theme).padding}`,
      color: theme.palette.ERROR,
      'border-color': theme.palette.ERROR,
    },
    labels: {
      base: {
        color: theme.palette.TEXT,
        'font-family': 'Arial',
        'font-size': `${formStyles(theme).labelFontSize}`,
        'font-weight': '1',
        'line-height': '16px',
        padding: `${formStyles(theme).labelPadding}`,
        margin: `${formStyles(theme).labelMarginTop} 0 0 0`,
      },
      invalid: {
        color: theme.palette.ERROR,
      },
    },
    'cvv-icon': {
      base: {
        display: 'none',
      },
    },
    errors: {
      invalid: {
        color: theme.palette.ERROR,
      },
    },
  },
})

interface WePayIframeProps {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  setWePay: Dispatch<WePayIframe>
  paymentMethod: PaymentMethod
  isWePaySDKLoaded: boolean
  divID?: string
  setHasWePayError: Dispatch<boolean>
  hasWePayError: boolean
}

const WePayIframe: FunctionComponent<WePayIframeProps> = ({
  divID = 'wepay-credit-card-iframe',
  paymentMethod,
  isWePaySDKLoaded,
  setWePay,
  setHasWePayError,
  hasWePayError,
}) => {
  const theme = useTheme()
  const router = useRouter()

  useEffect(() => {
    const isOnlinePayment = paymentMethod === PaymentMethod.ONLINE
    const iframeOptions = {
      custom_style: iframeStyles(theme),
      show_error_messages: true,
      show_placeholders: false,
      show_labels: true,
    }

    if (isWePaySDKLoaded && isOnlinePayment && !hasWePayError) {
      const iframeAlreadyExists =
        // eslint-disable-next-line no-restricted-properties
        document.getElementById(`${divID}_iframe`) != null
      try {
        const url = router.pathname
        const iframe =
          !url.includes('checkout') || iframeAlreadyExists
            ? null
            : WePay.createCreditCardIframe(divID, iframeOptions)

        if (iframe?.error_code) {
          datadogRum.addError(new Error(iframe?.error_code ?? 'WePay iframe error'))
          setHasWePayError(true)
          return
        }

        // making sure that if the iframe already exists and we've set the const iframe value to null, do not reset the wepay iframe local state to null
        if (!iframeAlreadyExists) {
          setWePay(iframe as WePayIframe)
        }
      } catch (error) {
        datadogRum.addError(error)
        setHasWePayError(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, divID, theme, isWePaySDKLoaded, hasWePayError])

  return <div id={divID} className={iframeDivStyles(theme)} />
}

export default WePayIframe
